import { ApiResponse, Role, UserLogin } from 'app/shared/models';
import {
  EncripterService,
  HttpClientService,
  UserLoginService,
} from 'app/shared/services';
import { catchError, Observable, of, switchMap, take, throwError } from 'rxjs';

/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { AuthUtils } from '../utils';

interface MfaChallenge {
  challengeId: string;
  userId: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private _authenticated: boolean = false;
  private _pendingMfaLogin: MfaChallenge | null = null;
  private _httpClient = inject(HttpClient);
  private _userLoginService = inject(UserLoginService);
  private _clientService = inject(HttpClientService);
  private _encriptor = inject(EncripterService);

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Setter & getter for access token
   */
  set accessToken(token: string) {
    const cripted = this._encriptor.set(null, token);
    localStorage.setItem('passport', cripted);
  }

  set email(value: string) {
    const cripted = this._encriptor.set(null, value);
    localStorage.setItem('email', cripted);
  }

  get email(): string {
    const plain = localStorage.getItem('email') ?? '';

    return this._encriptor.get(null, plain);
  }

  get accessToken(): string {
    const plain = localStorage.getItem('passport') ?? '';

    try {
      return this._encriptor.get(null, plain);
    } catch (e) {
      localStorage.removeItem('passport');
      localStorage.removeItem('lv');
      return '';
    }
  }

  set level(value: number | null | undefined) {
    const cripted = this._encriptor.set(null, value?.toString() ?? '0');
    localStorage.setItem('lv', cripted);
  }

  get level(): number {
    const plain = localStorage.getItem('lv') ?? '';

    return Number(this._encriptor.get(null, plain) ?? 0);
  }

  get role(): Role {
    let role: Role = undefined;

    this._userLoginService.user$
      .pipe(take(1))
      .subscribe((user) => (role = user.role));

    return role;
  }

  set partnerId(value: string | null | undefined) {
    const cripted = this._encriptor.set(null, value ?? '');
    localStorage.setItem('puid', cripted);
  }

  get partnerId(): string | null {
    const plain = localStorage.getItem('puid') ?? '';

    if (!plain) {
      return null;
    }

    return this._encriptor.get(null, plain) ?? null;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Forgot password
   *
   * @param email
   */
  forgotPassword(email: string): Observable<any> {
    return this._httpClient.post('api/auth/forgot-password', email);
  }

  /**
   * Reset password
   *
   * @param password
   */
  resetPassword(password: string): Observable<any> {
    return this._httpClient.post('api/auth/reset-password', password);
  }

  /**
   * Sign in
   *
   * @param credentials
   */
  signIn(credentials: {
    email: string;
    password: string;
    rememberMe: boolean;
  }): Observable<any> {
    // Throw error, if the user is already logged in
    if (this._authenticated) {
      return throwError(() => new Error('USER_IS_ALREADY_LOGGED_IN'));
    }

    this._pendingMfaLogin = null;

    return this._clientService
      .post<ApiResponse<UserLogin | MfaChallenge | null>>('/mfa/auth', {
        ...credentials,
        type: 'user',
        licensedId: 'ezvolt',
      })
      .pipe(
        switchMap((response: any) => {
          if (!response.success) {
            return throwError(() => new Error(this._getErrorMessage(response)));
          }

          if (credentials.rememberMe) {
            this.email = credentials.email;
          }

          if (response.data?.challengeId && response.data?.userId) {
            this._pendingMfaLogin = {
              challengeId: response.data.challengeId,
              userId: response.data.userId,
            };
          } else {
            this._processLogin(response);
          }

          // Return a new observable with the response
          return of(response);
        })
      );
  }

  /**
   * Check if there is a login waiting for MFA validation
   */
  hasPendingMfaLogin(): boolean {
    return this._pendingMfaLogin !== null;
  }

  /**
   * Validate MFA code
   */
  validateMfa(code: string): Observable<any> {
    if (!this._pendingMfaLogin) {
      return throwError(() => new Error('MFA_CHALLENGE_NOT_FOUND'));
    }

    return this._clientService
      .post<ApiResponse<UserLogin | null>>('/mfa/verify', {
        challengeId: this._pendingMfaLogin.challengeId,
        userId: this._pendingMfaLogin.userId,
        code,
      })
      .pipe(
        switchMap((response: any) => {
          if (!response.success) {
            return throwError(() => new Error(this._getErrorMessage(response)));
          }

          this._processLogin(response);
          this._pendingMfaLogin = null;

          return of(response);
        })
      );
  }

  /**
   * Resend MFA code
   */
  resendMfa(): Observable<any> {
    if (!this._pendingMfaLogin) {
      return throwError(() => new Error('MFA_CHALLENGE_NOT_FOUND'));
    }

    return this._clientService
      .post<ApiResponse<null>>('/mfa/resend', {
        challengeId: this._pendingMfaLogin.challengeId,
        userId: this._pendingMfaLogin.userId,
      })
      .pipe(
        switchMap((response: any) => {
          if (!response.success) {
            return throwError(() => new Error(this._getErrorMessage(response)));
          }

          return of(response);
        })
      );
  }

  private _getErrorMessage(response): string {
    const error = response?.error ?? response?.errors;

    if (typeof error === 'string') {
      return error;
    }

    if (Array.isArray(error)) {
      return error.filter((item) => typeof item === 'string').join(' ');
    }

    return typeof error?.message === 'string' ? error.message : '';
  }

  /**
   * Cancel the pending MFA login
   */
  cancelPendingMfaLogin(): void {
    this._pendingMfaLogin = null;
  }

  /**
   * Sign in using the access token
   */
  signInUsingToken(): Observable<any> {
    // Sign in using the token
    return this._clientService.post('/auth/sign-in-with-token', {}).pipe(
      catchError(() => {
        // Return false
        return of(false);
      }),
      switchMap((response: any) => {
        if (!response.success) {
          return of(false);
        }

        this._processLogin(response);

        // Return a new observable with the response
        return of(response);
      })
    );
  }

  private _processLogin(response) {
    // Store the access token in the local storage
    this.accessToken = response.data.token;

    // Set the authenticated flag to true
    this._authenticated = true;
    this.level = Number(response.data.level ?? 0);
    this.partnerId = response.data.partner?.id;

    // Store the user on the user service
    this._userLoginService.user = {
      id: response.data.userId,
      name: response.data.name,
      email: response.data.email,
      avatar: response.data.avatar,
      token: response.data.token,
      role: response.data.role,
      partnerId: response.data.partner?.id,
      status: 'online',
    };
  }

  /**
   * Sign out
   */
  signOut(): Observable<any> {
    localStorage.removeItem('passport');
    localStorage.removeItem('lv');
    localStorage.removeItem('fleetsChargePoints.locationId');

    this._authenticated = false;
    this._pendingMfaLogin = null;

    // Return the observable
    return of(true);
  }

  /**
   * Sign up
   *
   * @param user
   */
  signUp(user: {
    name: string;
    email: string;
    password: string;
    company: string;
  }): Observable<any> {
    return this._httpClient.post('api/auth/sign-up', user);
  }

  /**
   * Unlock session
   *
   * @param credentials
   */
  unlockSession(credentials: {
    email: string;
    password: string;
  }): Observable<any> {
    return this._httpClient.post('api/auth/unlock-session', credentials);
  }

  /**
   * Check the authentication status
   */
  check(): Observable<boolean> {
    // Check if the user is logged in
    if (this._authenticated) {
      return of(true);
    }

    // Check the access token availability
    if (!this.accessToken) {
      return of(false);
    }

    // Check the access token expire date
    if (AuthUtils.isTokenExpired(this.accessToken)) {
      return of(false);
    }

    return this.signInUsingToken();
  }
}
